import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, Field, FormActions, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { clean, formatMoment, isNumber, numberOrNull, openButtonClass, ProductChoice, ProductPicker, TableFooter, thClass, useListState } from './partApprovalShared';

interface Limits {
  centre: number;
  upper: number;
  lower: number;
}
interface Figures {
  subgroups: number;
  pieces: number;
  piecesOutOfSpec: number;
  grandMean: number | null;
  meanRange: number | null;
  meanChart: Limits | null;
  rangeChart: Limits | null;
  sigmaWithin: number | null;
  sigmaOverall: number | null;
  cp: number | null;
  cpk: number | null;
  pp: number | null;
  ppk: number | null;
  outOfControl: number;
  preliminary: boolean;
}
interface CharacteristicRow extends Figures {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  name: string;
  unit: string | null;
  nominal: number | null;
  lowerLimit: number | null;
  upperLimit: number | null;
  subgroupSize: number;
  isActive: boolean;
  lastSampledAt?: string | null;
}
interface Point {
  id: string;
  sampledAt: string;
  values: number[];
  notes: string | null;
  recordedBy: string | null;
  mean: number;
  range: number;
  meanOut: boolean;
  rangeOut: boolean;
  outOfSpec: boolean;
}
interface Characteristic extends CharacteristicRow {
  points: Point[];
  studySize: number;
}

const index = (value: number | null) => (value === null ? '—' : value.toFixed(2));
const specOf = (row: CharacteristicRow) => {
  const unit = row.unit ? ` ${row.unit}` : '';
  if (row.lowerLimit !== null && row.upperLimit !== null) return `${formatNumber(row.lowerLimit, 5)} to ${formatNumber(row.upperLimit, 5)}${unit}`;
  if (row.lowerLimit !== null) return `At least ${formatNumber(row.lowerLimit, 5)}${unit}`;
  if (row.upperLimit !== null) return `At most ${formatNumber(row.upperLimit, 5)}${unit}`;
  return 'No specification limits';
};

const spcApi = {
  page: async (params: object): Promise<{ data: CharacteristicRow[]; total: number; totalPages: number }> => (await api.get('/spc/characteristics', { params: clean(params) })).data,
  one: async (id: string): Promise<Characteristic> => (await api.get(`/spc/characteristics/${id}`)).data,
  save: async (id: string | null, data: object): Promise<Characteristic> => (id ? await api.put(`/spc/characteristics/${id}`, data) : await api.post('/spc/characteristics', data)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/spc/characteristics/${id}`);
  },
  addSubgroup: async (id: string, data: object): Promise<Characteristic> => (await api.post(`/spc/characteristics/${id}/subgroups`, data)).data,
  removeSubgroup: async (id: string, subgroupId: string): Promise<Characteristic> => (await api.delete(`/spc/characteristics/${id}/subgroups/${subgroupId}`)).data,
};

// ---------------------------------------------------------------- add or change a characteristic

const CharacteristicForm: React.FC<{ characteristic: Characteristic | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ characteristic, isPending, onCancel, onSubmit }) => {
  const [product, setProduct] = useState<ProductChoice | null>(characteristic ? { id: characteristic.productId, name: characteristic.productName, sku: characteristic.sku } : null);
  const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));
  const [form, setForm] = useState({ name: characteristic?.name ?? '', unit: characteristic?.unit ?? '', nominal: text(characteristic?.nominal), lowerLimit: text(characteristic?.lowerLimit), upperLimit: text(characteristic?.upperLimit), subgroupSize: String(characteristic?.subgroupSize ?? 5), isActive: characteristic?.isActive ?? true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const fixedSize = !!characteristic && characteristic.subgroups > 0;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!product) next.product = 'Choose the part';
    if (form.name.trim().length < 2) next.name = 'Name the characteristic';
    if (![form.nominal, form.lowerLimit, form.upperLimit].every(isNumber)) next.limits = 'Enter numbers';
    else if (form.lowerLimit.trim() !== '' && form.upperLimit.trim() !== '' && Number(form.lowerLimit) >= Number(form.upperLimit)) next.limits = 'The lower limit must be below the upper one';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ productId: product?.id, name: form.name.trim(), unit: form.unit.trim() || null, nominal: numberOrNull(form.nominal), lowerLimit: numberOrNull(form.lowerLimit), upperLimit: numberOrNull(form.upperLimit), subgroupSize: Number(form.subgroupSize), isActive: form.isActive });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Part" htmlFor="sc-product" required error={errors.product} className="sm:col-span-3">
          <ProductPicker id="sc-product" value={product} onChange={setProduct} disabled={!!characteristic} error={!!errors.product} />
        </Field>
        <Field label="Characteristic" htmlFor="sc-name" required error={errors.name} className="sm:col-span-2">
          <input id="sc-name" type="text" maxLength={120} value={form.name} onChange={set('name')} placeholder="Bore diameter" className={cn(inputClass, errors.name && inputErrorClass)} />
        </Field>
        <Field label="Unit" htmlFor="sc-unit"><input id="sc-unit" type="text" maxLength={20} value={form.unit} onChange={set('unit')} placeholder="mm" className={inputClass} /></Field>
        <Field label="Lower specification limit" htmlFor="sc-lower" error={errors.limits}><input id="sc-lower" type="number" step="any" value={form.lowerLimit} onChange={set('lowerLimit')} className={cn(inputClass, 'text-right tabular-nums', errors.limits && inputErrorClass)} /></Field>
        <Field label="Nominal" htmlFor="sc-nominal"><input id="sc-nominal" type="number" step="any" value={form.nominal} onChange={set('nominal')} className={cn(inputClass, 'text-right tabular-nums')} /></Field>
        <Field label="Upper specification limit" htmlFor="sc-upper"><input id="sc-upper" type="number" step="any" value={form.upperLimit} onChange={set('upperLimit')} className={cn(inputClass, 'text-right tabular-nums', errors.limits && inputErrorClass)} /></Field>
        <Field label="Pieces in a subgroup" htmlFor="sc-size" required hint={fixedSize ? 'Fixed once subgroups are recorded' : 'Measured each time; 5 is usual'}>
          <select id="sc-size" value={form.subgroupSize} disabled={fixedSize} onChange={set('subgroupSize')} className={inputClass}>
            {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((size) => (<option key={size} value={size}>{size}</option>))}
          </select>
        </Field>
        {characteristic && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Still charted
          </label>
        )}
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : characteristic ? 'Save Characteristic' : 'Add Characteristic'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one chart

const ControlChart: React.FC<{ title: string; data: { n: number; value: number; out: boolean }[]; limits: Limits | null; spec?: { lower: number | null; upper: number | null } }> = ({ title, data, limits, spec }) => {
  const values = [...data.map((point) => point.value), ...(limits ? [limits.upper, limits.lower] : [])];
  const low = Math.min(...values);
  const high = Math.max(...values);
  const pad = (high - low || Math.abs(high) || 1) * 0.15;
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h5>
      <div className="mt-2 h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 44, bottom: 0, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="n" tick={{ fontSize: 11 }} />
            <YAxis domain={[low - pad, high + pad]} tick={{ fontSize: 11 }} tickFormatter={(value) => Number(value).toFixed(3)} width={64} />
            <Tooltip formatter={(value) => [Number(value).toFixed(4), title]} labelFormatter={(label) => `Subgroup ${label}`} />
            {limits && <ReferenceLine y={limits.upper} stroke="#dc2626" strokeDasharray="4 3" label={{ value: 'UCL', position: 'right', fontSize: 10, fill: '#dc2626' }} />}
            {limits && <ReferenceLine y={limits.centre} stroke="#16a34a" label={{ value: 'CL', position: 'right', fontSize: 10, fill: '#16a34a' }} />}
            {limits && <ReferenceLine y={limits.lower} stroke="#dc2626" strokeDasharray="4 3" label={{ value: 'LCL', position: 'right', fontSize: 10, fill: '#dc2626' }} />}
            {spec?.upper != null && spec.upper <= high + pad && <ReferenceLine y={spec.upper} stroke="#6b7280" strokeDasharray="1 3" label={{ value: 'USL', position: 'right', fontSize: 10, fill: '#6b7280' }} />}
            {spec?.lower != null && spec.lower >= low - pad && <ReferenceLine y={spec.lower} stroke="#6b7280" strokeDasharray="1 3" label={{ value: 'LSL', position: 'right', fontSize: 10, fill: '#6b7280' }} />}
            <Line
              type="linear"
              dataKey="value"
              stroke="#4f46e5"
              strokeWidth={1.5}
              isAnimationActive={false}
              dot={(props: { cx?: number; cy?: number; index?: number; payload?: { out: boolean } }) => <circle key={props.index} cx={props.cx} cy={props.cy} r={props.payload?.out ? 5 : 3} fill={props.payload?.out ? '#dc2626' : '#4f46e5'} stroke="none" />}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- one characteristic: its charts and its subgroups

const CharacteristicPanel: React.FC<{ characteristicId: string; canRecord: boolean; canDelete: boolean; onEdit: (characteristic: Characteristic) => void; onClose: () => void; onChanged: () => void }> = ({ characteristicId, canRecord, canDelete, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: characteristic, isLoading, isError, error } = useQuery({ queryKey: ['spc', 'one', characteristicId], queryFn: () => spcApi.one(characteristicId) });
  const [values, setValues] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 });
  const store = (saved: Characteristic) => {
    queryClient.setQueryData(['spc', 'one', characteristicId], saved);
    onChanged();
  };
  const add = useMutation({
    mutationFn: (input: object) => spcApi.addSubgroup(characteristicId, input),
    onSuccess: (saved) => {
      const last = saved.points[saved.points.length - 1];
      if (last?.meanOut || last?.rangeOut) toast.error(`Subgroup ${saved.subgroups} is outside the control limits: the process has moved. Look for the cause.`, { duration: 9000 });
      else if (last?.outOfSpec) toast.error(`Subgroup ${saved.subgroups} has a piece outside the specification`, { duration: 8000 });
      else toast.success(`Subgroup ${saved.subgroups} recorded`);
      store(saved);
      setValues([]);
      setNotes('');
    },
    onError: fail,
  });
  const removeGroup = useMutation({
    mutationFn: (subgroupId: string) => spcApi.removeSubgroup(characteristicId, subgroupId),
    onSuccess: (saved) => {
      toast.success('Subgroup removed');
      store(saved);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => spcApi.remove(characteristicId),
    onSuccess: () => {
      toast.success('Characteristic deleted');
      onChanged();
      onClose();
    },
    onError: fail,
  });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !characteristic) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The characteristic could not be loaded.')}</p>;
  const size = characteristic.subgroupSize;
  const inputs = Array.from({ length: size }, (_, position) => values[position] ?? '');
  const record = () => {
    if (inputs.some((value) => value.trim() === '' || !Number.isFinite(Number(value)))) return setFormError(`Enter all ${size} values`);
    setFormError('');
    add.mutate({ values: inputs.map(Number), notes: notes.trim() || null });
  };
  const points = characteristic.points;
  const figures = [
    { label: 'Mean', value: characteristic.grandMean === null ? '—' : formatNumber(characteristic.grandMean, 4) },
    { label: 'Average range', value: characteristic.meanRange === null ? '—' : formatNumber(characteristic.meanRange, 4) },
    { label: 'Cp', value: index(characteristic.cp) },
    { label: 'Cpk', value: index(characteristic.cpk) },
    { label: 'Pp', value: index(characteristic.pp) },
    { label: 'Ppk', value: index(characteristic.ppk) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        {!characteristic.isActive && <span className={cn(badgeClass, 'bg-gray-50 text-gray-500 ring-gray-500/20')}>No longer charted</span>}
        <span className="font-semibold text-gray-900">{characteristic.productName} <span className="font-normal text-gray-500">{characteristic.sku}</span></span>
        <span>Specification {specOf(characteristic)}</span>
        <span>Subgroups of {size}</span>
        <span>{characteristic.subgroups} subgroup(s), {formatNumber(characteristic.pieces)} piece(s)</span>
      </div>
      <div className="grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-6">
        {figures.map((figure) => (<div key={figure.label}><p className="text-xs uppercase tracking-wide text-gray-500">{figure.label}</p><p className="font-medium text-gray-900 tabular-nums">{figure.value}</p></div>))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
        <span className={characteristic.outOfControl > 0 ? 'font-medium text-red-600' : 'text-green-700'}>{characteristic.outOfControl > 0 ? `${characteristic.outOfControl} subgroup(s) outside the control limits` : characteristic.meanChart ? 'Every subgroup is within the control limits' : 'Two subgroups are needed before there are control limits'}</span>
        {characteristic.piecesOutOfSpec > 0 && <span className="font-medium text-red-600">{characteristic.piecesOutOfSpec} piece(s) outside the specification</span>}
        {characteristic.preliminary && characteristic.meanChart && <span className="text-gray-500">Fewer than {characteristic.studySize} subgroups: the limits and indices are a first look, not yet a study</span>}
      </div>

      {points.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ControlChart title="Mean of each subgroup" data={points.map((point, position) => ({ n: position + 1, value: point.mean, out: point.meanOut }))} limits={characteristic.meanChart} spec={{ lower: characteristic.lowerLimit, upper: characteristic.upperLimit }} />
          <ControlChart title="Range of each subgroup" data={points.map((point, position) => ({ n: position + 1, value: point.range, out: point.rangeOut }))} limits={characteristic.rangeChart} />
        </div>
      )}

      {canRecord && characteristic.isActive && (
        <section className="rounded-lg border border-gray-200 p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Record a subgroup of {size} pieces{characteristic.unit ? `, in ${characteristic.unit}` : ''}</h4>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {inputs.map((value, position) => (
              <input key={position} type="number" step="any" value={value} onChange={(event) => { setValues(inputs.map((existing, at) => (at === position ? event.target.value : existing))); setFormError(''); }} aria-label={`Piece ${position + 1}`} placeholder={`#${position + 1}`} className={cn(inputClass, 'w-24 py-1 text-right tabular-nums')} />
            ))}
            <input type="text" maxLength={300} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Notes: shift, tool change…" aria-label="Notes on the subgroup" className={cn(inputClass, 'min-w-[10rem] flex-1 py-1')} />
            <button type="button" onClick={record} disabled={add.isPending} className={primaryButtonClass}>{add.isPending ? 'Saving…' : 'Record Subgroup'}</button>
          </div>
          {formError && <p className="mt-1 text-sm text-red-600">{formError}</p>}
        </section>
      )}

      {points.length > 0 && (
        <div className="max-h-64 overflow-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>#</th>
                <th scope="col" className={thClass}>Taken</th>
                <th scope="col" className={thClass}>Values</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Mean</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Range</th>
                <th scope="col" className={thClass}>Notes</th>
                <th scope="col" className={thClass}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...points].map((point, position) => ({ point, number: position + 1 })).reverse().map(({ point, number }) => (
                <tr key={point.id} className={cn((point.meanOut || point.rangeOut) && 'bg-red-50/60')}>
                  <td className="px-4 py-1.5 text-sm text-gray-500">{number}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 text-sm text-gray-700">{formatMoment(point.sampledAt)}</td>
                  <td className="px-4 py-1.5 text-sm text-gray-700 tabular-nums">{point.values.map((value) => formatNumber(value, 5)).join(' · ')}{point.outOfSpec && <span className="ml-2 text-xs font-medium text-red-600">out of specification</span>}</td>
                  <td className={cn('whitespace-nowrap px-4 py-1.5 text-right text-sm tabular-nums', point.meanOut ? 'font-semibold text-red-600' : 'text-gray-900')}>{formatNumber(point.mean, 4)}</td>
                  <td className={cn('whitespace-nowrap px-4 py-1.5 text-right text-sm tabular-nums', point.rangeOut ? 'font-semibold text-red-600' : 'text-gray-900')}>{formatNumber(point.range, 4)}</td>
                  <td className="max-w-xs truncate px-4 py-1.5 text-sm text-gray-600">{[point.notes, point.recordedBy && `by ${point.recordedBy}`].filter(Boolean).join(' · ') || '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canRecord && (
                      <button type="button" onClick={() => removeGroup.mutate(point.id)} disabled={removeGroup.isPending} title="Remove this subgroup, recorded by mistake" aria-label={`Remove subgroup ${number}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500">The control limits are the mean of the subgroup means plus and minus A2 times the average range, and D3 and D4 times the average range. Cp and Cpk take the spread from the average range; Pp and Ppk take it from all the pieces together. What value is good enough is for you and your customer to say.</p>

      <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
        <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
        {canDelete && characteristic.subgroups === 0 && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>}
        {canRecord && <button type="button" onClick={() => onEdit(characteristic)} className={secondaryButtonClass}>Edit</button>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the tab

export const Spc: React.FC<{ canCreate: boolean; canRecord: boolean; canDelete: boolean }> = ({ canCreate, canRecord, canDelete }) => {
  const queryClient = useQueryClient();
  const list = useListState();
  const [showOff, setShowOff] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Characteristic | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['spc', 'page', { page: list.page, limit: list.limit, search: list.search, showOff }],
    queryFn: () => spcApi.page({ page: list.page, limit: list.limit, search: list.search, activeOnly: showOff ? 'false' : undefined }),
    placeholderData: keepPreviousData,
  });
  const { data: viewing } = useQuery({ queryKey: ['spc', 'one', viewingId], queryFn: () => spcApi.one(viewingId as string), enabled: !!viewingId });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['spc', 'page'] });
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => spcApi.save(editing?.id ?? null, input),
    onSuccess: (characteristic) => {
      toast.success(editing ? `${characteristic.name} saved` : `${characteristic.name} added`);
      queryClient.setQueryData(['spc', 'one', characteristic.id], characteristic);
      refresh();
      closeForm();
      setViewingId(characteristic.id);
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The characteristic could not be saved'), { duration: 7000 }),
  });
  const rows = data?.data ?? [];

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="search" value={list.searchInput} onChange={(event) => list.setSearchInput(event.target.value)} placeholder="Search by characteristic or part" aria-label="Search characteristics" className={cn(inputClass, 'pl-9')} />
        </div>
        <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
          <input type="checkbox" checked={showOff} onChange={(event) => { setShowOff(event.target.checked); list.setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          Also those no longer charted
        </label>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Characteristic
          </button>
        )}
      </div>
      <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Characteristic</th>
              <th scope="col" className={thClass}>Specification</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Subgroups</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Cp</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Cpk</th>
              <th scope="col" className={thClass}>Control</th>
              <th scope="col" className={thClass}>Last taken</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isError ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The characteristics could not be loaded.')}</td></tr>
            ) : isLoading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-900">{list.search ? 'No characteristic matches' : 'No characteristics are charted yet'}</p>
                  <p className="mt-1 text-sm text-gray-500">Add a characteristic that is measured in subgroups, and record the subgroups as they are taken.</p>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={cn('hover:bg-gray-50', !row.isActive && 'bg-gray-50/60')}>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => setViewingId(row.id)} className="text-left text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.name}</button>
                    <p className="mt-0.5 text-xs text-gray-500">{row.productName} · {row.sku}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{specOf(row)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{row.subgroups}<span className="text-gray-500"> × {row.subgroupSize}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-900 tabular-nums">{index(row.cp)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{index(row.cpk)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">{row.outOfControl > 0 ? <span className="font-medium text-red-600">{row.outOfControl} out of control</span> : row.meanChart ? <span className="text-green-700">In control</span> : <span className="text-gray-400">Not enough data</span>}{row.piecesOutOfSpec > 0 && <span className="block text-xs font-medium text-red-600">{row.piecesOutOfSpec} piece(s) out of specification</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatMoment(row.lastSampledAt)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right"><button type="button" onClick={() => setViewingId(row.id)} className={openButtonClass} aria-label={`Open ${row.name}`}>Open</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TableFooter page={list.page} limit={list.limit} total={data?.total ?? 0} totalPages={data?.totalPages ?? 1} onPage={list.setPage} onLimit={list.setLimit} />

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.name}` : 'Add a Characteristic to Chart') : viewing?.name ?? 'Characteristic'} size={formOpen ? 'xl' : '2xl'}>
        {formOpen ? (
          <CharacteristicForm key={editing?.id ?? 'new'} characteristic={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && <CharacteristicPanel key={viewingId} characteristicId={viewingId} canRecord={canRecord} canDelete={canDelete} onEdit={(characteristic) => { setEditing(characteristic); setFormOpen(true); }} onClose={() => setViewingId(null)} onChanged={refresh} />
        )}
      </Modal>
    </>
  );
};
